/**
 * Typed API client for SpendLens.
 *
 * All requests include the wos-access-token cookie automatically (HttpOnly).
 * The backend reads the cookie and attaches the Authorization header via middleware.
 *
 * NOTE: The auth cookie is HttpOnly so JS can't read it — the backend extracts
 * it from the cookie header. For protected API calls we send credentials: 'include'.
 */

const BASE = `${import.meta.env.VITE_API_BASE_URL ?? ''}/api`;

async function request<T>(
  path: string,
  options: RequestInit = {},
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    ...options,
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      ...(options.headers ?? {}),
    },
  });

  if (res.status === 401) {
    window.location.href = '/login';
    throw new Error('Unauthorized');
  }

  if (!res.ok) {
    let message = `API error ${res.status}`;
    try {
      const body = await res.json();
      message = body.error ?? body.message ?? message;
    } catch {}
    throw new Error(message);
  }

  // 204 No Content
  if (res.status === 204) return undefined as T;

  return res.json();
}

// ── Auth ──────────────────────────────────────────────────────────
import type { CurrentUser } from '../types';

export const authApi = {
  me: () => request<CurrentUser>('/auth/me'),
  sendOtp: (email: string) =>
    request<{ ok: true }>('/auth/send-otp', {
      method: 'POST',
      body: JSON.stringify({ email }),
    }),
  verifyOtp: (email: string, code: string) =>
    request<{ ok: true }>('/auth/verify-otp', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    }),
};

// ── Departments ────────────────────────────────────────────────────
import type { Department } from '../types';

export const departmentsApi = {
  list: () => request<Department[]>('/departments'),
};

// ── Vendors ────────────────────────────────────────────────────────
import type { Vendor, VendorRegistryItem, SavingsDashboard, ClientTenant } from '../types';

export const vendorsApi = {
  list: () => request<Vendor[]>('/vendors'),
  registry: (tenantId?: string) =>
    request<VendorRegistryItem[]>(`/vendors/registry${tenantId ? `?tenantId=${tenantId}` : ''}`),
};

// ── Tenants ─────────────────────────────────────────────────────────

export const tenantsApi = {
  list: () => request<ClientTenant[]>('/tenants'),
};

// ── Dashboard ───────────────────────────────────────────────────────
import type { DashboardStats } from '../types';

export const dashboardApi = {
  stats: (tenantId?: string) =>
    request<DashboardStats>(`/dashboard/stats${tenantId ? `?tenantId=${tenantId}` : ''}`),
  savings: (tenantId?: string) =>
    request<SavingsDashboard>(`/dashboard/savings${tenantId ? `?tenantId=${tenantId}` : ''}`),
};

// ── Purchase Requests ─────────────────────────────────────────────
import type { PurchaseRequest, CreatePurchaseRequestDto } from '../types';

export const purchaseRequestsApi = {
  list: () => request<PurchaseRequest[]>('/purchase-requests'),
  get: (id: string) => request<PurchaseRequest>(`/purchase-requests/${id}`),
  create: (dto: CreatePurchaseRequestDto) =>
    request<PurchaseRequest>('/purchase-requests', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),
  submit: (id: string) =>
    request<PurchaseRequest>(`/purchase-requests/${id}/submit`, { method: 'PATCH' }),
  cancel: (id: string) =>
    request<PurchaseRequest>(`/purchase-requests/${id}/cancel`, { method: 'PATCH' }),
};

// ── Approvals ─────────────────────────────────────────────────────
import type { ApprovalStep } from '../types';

export const approvalsApi = {
  inbox: () => request<ApprovalStep[]>('/approvals/inbox'),
  stepsForPr: (prId: string) => request<ApprovalStep[]>(`/approvals/steps/${prId}`),
  decide: (stepId: string, decision: 'approve' | 'reject', comments?: string) =>
    request<{ success: boolean }>(`/approvals/${stepId}/decide`, {
      method: 'POST',
      body: JSON.stringify({ decision, comments }),
    }),
};

// ── Benchmarking ───────────────────────────────────────────────────

export const benchmarkingApi = {
  run: (tenantId: string) =>
    request<{ runId: string }>(`/benchmarking/run/${tenantId}`, { method: 'POST' }),
};

// ── SuperAdmin ─────────────────────────────────────────────────────

export interface SuperAdminTenant {
  id: string;
  name: string;
  slug: string;
  emailDomain: string | null;
  workosOrgId: string | null;
  isActive: boolean;
  offboardedAt: string | null;
  offboardedById: string | null;
  onboardingStage: number | null;
  customerStatus: string | null;
  displayCurrency: string;
  region: string;
  createdAt: string;
}

export interface TenantOnboardingDetail {
  tenant: {
    id: string;
    name: string;
    slug: string;
    emailDomain: string | null;
    displayCurrency: string;
    region: string;
    onboardingStage: number;
    customerStatus: string | null;
    createdAt: string;
    hqCountry: string | null;
  };
  stageData: {
    excelSent:    { completedAt: string | null; notes: string | null };
    dataUploaded: { completedAt: string | null; vendorCount: number; currenciesFound: string[]; totalAcvUsd: number };
    benchmarked:  { completedAt: string | null; runId: string | null; totalAssessedAcvUsd: number; savingsLowUsd: number; savingsHighUsd: number; vendorCount: number };
    proposalSent: { completedAt: string | null; proposalAmount: number | null; savingsOffered: number | null; notes: string | null };
    poReceived:   { completedAt: string | null; poNumber: string | null; poDate: string | null; notes: string | null };
    activating:   { completedAt: string | null };
    live:         { completedAt: string | null };
  };
  auditLog: Array<{
    action: string;
    metadata: Record<string, unknown> | null;
    actorEmail: string | null;
    createdAt: string;
  }>;
}

export const superadminApi = {
  uploadVendors: async (
    tenantId: string,
    file: File,
  ): Promise<{ rowsImported: number; rowsSkipped: number; skippedReasons?: Array<{ row: number; reason: string }>; currenciesFound?: string[] }> => {
    const form = new FormData();
    form.append('file', file);
    const res = await fetch(`${BASE}/superadmin/upload-vendors/${tenantId}`, {
      method: 'POST',
      credentials: 'include',
      body: form,
    });
    if (res.status === 401) {
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.error ?? `Upload failed (${res.status})`);
    }
    return res.json();
  },

  tenants: (): Promise<SuperAdminTenant[]> =>
    request<SuperAdminTenant[]>('/superadmin/tenants'),

  offboard: (tenantId: string): Promise<{ success: boolean; message: string }> =>
    request(`/superadmin/tenants/${tenantId}/offboard`, { method: 'POST' }),

  /**
   * Hard-delete a never-promoted prospect. DRY RUN unless confirm=true:
   * without confirm the API returns per-table counts and deletes nothing.
   */
  hardDeleteProspect: (
    tenantId: string,
    confirm: boolean,
  ): Promise<{
    success: boolean;
    mode: 'dry_run' | 'deleted';
    tenantSlug: string;
    counts: Record<string, number>;
    message: string;
  }> =>
    request(
      `/superadmin/tenants/${tenantId}/hard-delete${confirm ? '?confirm=true' : ''}`,
      { method: 'DELETE' },
    ),

  onboard: (dto: {
    name: string;
    emailDomain: string;
    portalAdminEmail: string;
    displayCurrency: string;
    region: string;
    hqCountry: string;
    estimatedSpend?: string;
    source?: string;
    notes?: string;
  }): Promise<{ tenant: SuperAdminTenant; message: string }> =>
    request('/superadmin/tenants/onboard', {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  updateStage: (tenantId: string, stage: number): Promise<SuperAdminTenant> =>
    request(`/superadmin/tenants/${tenantId}/stage`, {
      method: 'PATCH',
      body: JSON.stringify({ stage }),
    }),

  onboardingDetail: (tenantId: string): Promise<TenantOnboardingDetail> =>
    request<TenantOnboardingDetail>(`/superadmin/tenants/${tenantId}/onboarding-detail`),

  runBenchmarking: (tenantId: string): Promise<{ runId: string; status: string; vendorCount: number; estimatedDurationSeconds: number }> =>
    request(`/superadmin/tenants/${tenantId}/run-benchmarking`, { method: 'POST' }),

  benchmarkStatus: (tenantId: string, runId: string): Promise<{
    runId: string;
    status: 'pending' | 'running' | 'complete' | 'failed';
    completedVendors: number;
    totalVendors: number;
    errorMessage?: string;
  }> =>
    request(`/superadmin/tenants/${tenantId}/benchmark-status?runId=${encodeURIComponent(runId)}`),

  markProposalSent: (
    tenantId: string,
    dto: { proposalAmount?: number | null; savingsOffered?: number | null; notes?: string | null },
  ): Promise<SuperAdminTenant> =>
    request(`/superadmin/tenants/${tenantId}/mark-proposal-sent`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  markPoReceived: (
    tenantId: string,
    dto: { poNumber: string; poDate: string; notes?: string | null },
  ): Promise<SuperAdminTenant> =>
    request(`/superadmin/tenants/${tenantId}/mark-po-received`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  promoteToCustomer: (
    tenantId: string,
    dto: {
      portalAdminEmail: string;
      portalAdminRole: string;
      portalAdminName?: string;
      guaranteedSavingsAmount: number;
      guaranteedSavingsCurrency: string;
      spendUnderManagement: number;
      spendUnderManagementCurrency: string;
      engagementFeeModel: string;
      engagementFeeValue: number;
      engagementStartDate: string;
      engagementEndDate?: string | null;
      paymentTerms: string;
      vendorsInScopeCount: number;
      categoriesInScope: string[];
      benchmarkRefreshCadence: string;
      msaDocumentUrl?: string | null;
      signedByEmail: string;
      invitationExpiresInDays?: number;
    },
  ): Promise<{
    tenant: SuperAdminTenant;
    engagementId: string;
    invitationId: string;
    invitedUserEmail: string;
    message: string;
  }> =>
    request(`/superadmin/tenants/${tenantId}/promote-to-customer`, {
      method: 'POST',
      body: JSON.stringify(dto),
    }),

  revertStage: (tenantId: string, targetStage: number): Promise<SuperAdminTenant> =>
    request(`/superadmin/tenants/${tenantId}/revert-stage`, {
      method: 'PATCH',
      body: JSON.stringify({ targetStage }),
    }),

  exportTenant: async (tenantId: string, slug: string): Promise<void> => {
    const res = await fetch(`${BASE}/superadmin/tenants/${tenantId}/export`, {
      credentials: 'include',
    });
    if (res.status === 401) {
      window.location.href = '/login';
      throw new Error('Unauthorized');
    }
    if (!res.ok) throw new Error(`Export failed (${res.status})`);
    const blob = await res.blob();
    const url  = URL.createObjectURL(blob);
    const a    = document.createElement('a');
    a.href     = url;
    a.download = `spendlens-export-${slug}-${new Date().toISOString().slice(0, 10)}.zip`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

// ── Purchase Orders ────────────────────────────────────────────────
import type { PurchaseOrder } from '../types';

export const purchaseOrdersApi = {
  list: () => request<PurchaseOrder[]>('/purchase-orders'),
  getByPr: (prId: string) => request<PurchaseOrder | null>(`/purchase-orders/by-pr/${prId}`),
};
